import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, LogIn, ArrowRight, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUserStore } from '../stores/userStore';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import toast from 'react-hot-toast';
import BackgroundCircles from '../components/BackgroundCircles';
import type { UserRole } from '../types/user';
import type { PlanType } from '../types/subscription';

interface PlanDetail {
  name: string;
  type: PlanType;
  isYearly: boolean;
  role: UserRole;
}

export default function SuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const updateUserRole = useUserStore(state => state.updateUserRole);
  const updateSubscription = useSubscriptionStore(state => state.updateSubscription);
  
  const [planDetails, setPlanDetails] = useState<PlanDetail | null>(null);
  const [processed, setProcessed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract plan parameter from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const plan = params.get('plan');
    
    if (!plan) {
      setError('Não foi possível detectar o plano. Por favor, entre em contato com o suporte.');
      return;
    }

    let planName = '';
    let planType: PlanType = 'free';
    let isYearly = false;
    let role: UserRole = 'user';

    switch (plan) {
      case 'pro':
        planName = 'Pro';
        planType = 'pro';
        role = 'pro';
        isYearly = false;
        break;
      case 'premium':
        planName = 'Premium';
        planType = 'premium';
        role = 'premium';
        isYearly = false;
        break;
      case 'pro_yearly':
        planName = 'Pro Anual';
        planType = 'pro_yearly';
        role = 'pro_yearly';
        isYearly = true;
        break;
      case 'premium_yearly':
        planName = 'Premium Anual';
        planType = 'premium_yearly';
        role = 'premium_yearly';
        isYearly = true;
        break;
      default:
        setError('Plano não reconhecido. Por favor, entre em contato com o suporte.');
        return;
    }

    setPlanDetails({
      name: planName,
      type: planType,
      isYearly,
      role
    });
  }, [location.search]);

  // Process the subscription when user is available and plan details are set
  useEffect(() => {
    const processSubscription = async () => {
      if (user?.email && planDetails && !processed && !isProcessing) {
        try {
          setIsProcessing(true);
          setError(null);
          
          console.log('Processing subscription:', {
            email: user.email,
            planType: planDetails.type,
            role: planDetails.role
          });
          
          // Update user role
          updateUserRole(user.email, planDetails.role);
          
          // Update subscription store
          updateSubscription({
            planId: planDetails.type,
            isActive: true,
            cancelAtPeriodEnd: false,
            currentPeriodEnds: planDetails.isYearly 
              ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
              : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
          });

          toast.success(`Plano ${planDetails.name} ativado com sucesso!`);
          setProcessed(true);
        } catch (err) {
          console.error('Error processing subscription:', err);
          setError('Ocorreu um erro ao ativar seu plano. Por favor, entre em contato com o suporte.');
          toast.error('Erro ao ativar plano');
        } finally {
          setIsProcessing(false);
        }
      }
    };

    processSubscription();
  }, [user, planDetails, processed, isProcessing, updateUserRole, updateSubscription]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleLogin = () => {
    // Redirect to login page with return URL
    const returnUrl = encodeURIComponent(`/success${location.search}`);
    navigate(`/login?returnUrl=${returnUrl}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative bg-primary-600">
      <BackgroundCircles />
      
      <div className="max-w-md w-full mx-4 bg-white rounded-xl p-8 shadow-xl relative z-10">
        {error ? (
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Ocorreu um erro
            </h1>
            <p className="text-gray-700 mb-6">
              {error}
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-3 px-4 bg-primary-600 text-white rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all transform hover:scale-[1.02] font-medium"
            >
              Voltar ao Dashboard
            </button>
          </div>
        ) : !planDetails ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : user ? (
          <>
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                🎉 Seu plano Kontty {planDetails.name} está ativo!
              </h1>
              <p className="text-gray-700">
                Obrigado por escolher a Kontty. Você agora tem acesso a todos os recursos do plano {planDetails.name}.
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h2 className="font-medium text-gray-900 mb-2">Detalhes da assinatura:</h2>
                <ul className="space-y-2 text-gray-700">
                  <li>• Plano: <span className="font-medium">{planDetails.name}</span></li>
                  <li>• Período: <span className="font-medium">{planDetails.isYearly ? 'Anual' : 'Mensal'}</span></li>
                  <li>• Status: <span className="text-green-600 font-medium">Ativo</span></li>
                </ul>
              </div>

              <button
                onClick={handleGoToDashboard}
                disabled={isProcessing || !processed}
                className="w-full py-3 px-4 bg-green-500 text-white rounded-xl hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] font-medium flex items-center justify-center"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processando...
                  </>
                ) : (
                  <>
                    Ir para o Dashboard
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <LogIn className="h-8 w-8 text-indigo-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Faça login para ativar seu plano
              </h1>
              <p className="text-gray-700">
                Para ativar seu plano Kontty {planDetails?.name}, por favor faça login na sua conta.
              </p>
            </div>

            <button
              onClick={handleLogin}
              className="w-full py-3 px-4 bg-primary-600 text-white rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all transform hover:scale-[1.02] font-medium flex items-center justify-center"
            >
              Fazer Login
              <LogIn className="ml-2 h-5 w-5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}